/**
 * Cache and Session Management Utilities
 * Handles cache clearing, session management, and storage cleanup
 */

// Cache keys used throughout the application
export const CACHE_KEYS = {
  USER: 'hype-circle-user',
  AUTH_STATE: 'hype-circle-auth-state',
  CACHE_TIMESTAMP: 'hype-circle-cache-timestamp',
  LAST_ACTIVITY: 'hype-circle-last-activity',
  THEME_MODE: 'theme-mode',
  FEED_CACHE: 'hype-circle-feed-cache',
  MOVIE_CACHE: 'hype-circle-movie-cache',
  SEARCH_HISTORY: 'hype-circle-search-history',
  NOTIFICATION_SETTINGS: 'hype-circle-notification-settings',
  BADGE_SETTINGS: 'hype-circle-badge-settings',
};

/**
 * Clear all authentication-related cache
 */
export const clearAuthCache = () => {
  const authKeys = [
    CACHE_KEYS.USER,
    CACHE_KEYS.AUTH_STATE,
    CACHE_KEYS.CACHE_TIMESTAMP,
    CACHE_KEYS.LAST_ACTIVITY,
  ];
  
  authKeys.forEach(key => {
    localStorage.removeItem(key);
  });
  
  console.log('Auth cache cleared');
};

/**
 * Clear all application cache (except theme preferences)
 */
export const clearAllCache = () => {
  const preserveKeys = [CACHE_KEYS.THEME_MODE];
  
  Object.values(CACHE_KEYS).forEach(key => {
    if (!preserveKeys.includes(key)) {
      localStorage.removeItem(key);
    }
  });
  
  console.log('All cache cleared (theme preserved)');
};

/**
 * Clear browser cookies for the current domain
 */
export const clearCookies = () => {
  // Get all cookies
  const cookies = document.cookie.split(';');
  
  // Clear each cookie
  cookies.forEach(cookie => {
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
    
    // Clear cookie for current domain
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname}`;
  });
  
  console.log('Cookies cleared');
};

/**
 * Clear IndexedDB data (used by AWS Amplify)
 */
export const clearIndexedDB = async () => {
  try {
    // Get all databases
    if ('databases' in indexedDB) {
      const databases = await indexedDB.databases();
      
      // Delete each database
      await Promise.all(
        databases.map(db => {
          return new Promise((resolve, reject) => {
            const deleteReq = indexedDB.deleteDatabase(db.name);
            deleteReq.onsuccess = () => resolve();
            deleteReq.onerror = () => reject(deleteReq.error);
          });
        })
      );
    }
    
    console.log('IndexedDB cleared');
  } catch (error) {
    console.warn('Failed to clear IndexedDB:', error);
  }
};

/**
 * Clear session storage
 */
export const clearSessionStorage = () => {
  sessionStorage.clear();
  console.log('Session storage cleared');
};

/**
 * Force clear all browser data
 */
export const forceCleanBrowser = async () => {
  try {
    // Clear all cache types
    clearAllCache();
    clearCookies();
    clearSessionStorage();
    await clearIndexedDB();
    
    // Clear service worker cache if available
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      console.log('Service worker cache cleared');
    }
    
    console.log('Browser data force cleaned');
    return true;
  } catch (error) {
    console.error('Failed to force clean browser:', error);
    return false;
  }
};

/**
 * Check if cache is expired
 */
export const isCacheExpired = (cacheKey, maxAge = 24 * 60 * 60 * 1000) => {
  try {
    const timestamp = localStorage.getItem(`${cacheKey}-timestamp`);
    if (!timestamp) return true;
    
    return Date.now() - parseInt(timestamp) > maxAge;
  } catch {
    return true;
  }
};

/**
 * Set cache with timestamp
 */
export const setCacheWithTimestamp = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    localStorage.setItem(`${key}-timestamp`, Date.now().toString());
  } catch (error) {
    console.warn(`Failed to set cache for ${key}:`, error);
  }
};

/**
 * Get cache with expiry check
 */
export const getCacheWithExpiry = (key, maxAge = 24 * 60 * 60 * 1000) => {
  try {
    if (isCacheExpired(key, maxAge)) {
      localStorage.removeItem(key);
      localStorage.removeItem(`${key}-timestamp`);
      return null;
    }
    
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
};

/**
 * Debug function to show all stored data
 */
export const debugStorage = () => {
  console.group('Storage Debug Info');
  
  console.log('LocalStorage:');
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    const value = localStorage.getItem(key);
    console.log(`  ${key}:`, value?.length > 100 ? `${value.substring(0, 100)}...` : value);
  }
  
  console.log('SessionStorage:');
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    const value = sessionStorage.getItem(key);
    console.log(`  ${key}:`, value?.length > 100 ? `${value.substring(0, 100)}...` : value);
  }
  
  console.log('Cookies:');
  document.cookie.split(';').forEach(cookie => {
    console.log(`  ${cookie.trim()}`);
  });
  
  console.groupEnd();
};

/**
 * Get storage usage information
 */
export const getStorageInfo = () => {
  const info = {
    localStorage: {
      used: 0,
      keys: []
    },
    sessionStorage: {
      used: 0,
      keys: []
    }
  };
  
  // Calculate localStorage usage
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    const value = localStorage.getItem(key);
    info.localStorage.used += key.length + (value?.length || 0);
    info.localStorage.keys.push(key);
  }
  
  // Calculate sessionStorage usage
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    const value = sessionStorage.getItem(key);
    info.sessionStorage.used += key.length + (value?.length || 0);
    info.sessionStorage.keys.push(key);
  }
  
  return info;
};

// Export for global access in development
if (typeof window !== 'undefined') {
  window.cacheUtils = {
    clearAuthCache,
    clearAllCache,
    clearCookies,
    clearIndexedDB,
    clearSessionStorage,
    forceCleanBrowser,
    debugStorage,
    getStorageInfo,
  };
}